//
// # Applying a Wire Plan
//
// Turns the index-based edits from `planWireEdits` into live geometry plus
// change-log entries. Kept separate from the planner so the planning rules
// stay pure and testable, and separate from the modes so both of them apply a
// plan identically.
//

import { Point, model, schdata, graphics } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import type { SchematicRender } from "DrawCore/src/content/schematic/render";
import type { WireSegment } from "DrawCore/src/content/schematic/wire";
import { Wires } from "DrawCore/src/content/schematic/wires";
import { pointKey } from "DrawCore/src/routing";
import type { WireEdit } from "DrawCore/src/routing/wire_plan";
import { describeWireViolations } from "DrawCore/src/routing/invariants";
import {
  describeNetChanges,
  netsOf,
  type NetPin,
} from "DrawCore/src/routing/connectivity";

// The live segments a plan refers to, index-aligned with the `wires` array
// handed to the planner, alongside the geometry they had before the gesture.
export interface WireEditTargets {
  segments: WireSegment[];
  origEndpoints: Array<[Point, Point]>;
  origLabels: Array<Array<graphics.Text>>;
}

// Snapshot the schematic's wires into planner-aligned arrays. Call this while
// the geometry still holds its pre-gesture values.
export function snapshotWires(schematic: SchematicRender): WireEditTargets {
  const segments = Array.from(schematic.wires.all());
  return {
    segments,
    origEndpoints: segments.map((seg) => [seg.start(), seg.end()]),
    origLabels: segments.map((seg) =>
      model.schematic.cloneWireLabels(seg.data.labels)
    ),
  };
}

// Apply `edits`, returning the change-log entries the caller should combine
// into a single undo step.
//
// Every mutation produces an entry — including deletions, which is what a drag
// that collapsed a wire used to skip, putting the result beyond the reach of
// undo.
//
// Creates go last: `addMerging` can retire a segment that a later edit still
// names by index into the pre-gesture snapshot. Nothing indexes a created
// segment, so deferring them is free.
export function applyWireEdits(
  schematic: SchematicRender,
  targets: WireEditTargets,
  edits: WireEdit[]
): Array<changes.Single<WireSegment>> {
  const { segments, origEndpoints, origLabels } = targets;
  const out: Array<changes.Single<WireSegment>> = [];

  for (const edit of edits) {
    switch (edit.kind) {
      case "create":
        break; // Second pass, below.
      case "update": {
        const seg = segments[edit.index];
        const [start, end] = edit.endpoints;
        const labels = model.schematic.recenterLabels(
          origLabels[edit.index] ?? [],
          start,
          end
        );
        seg.update({ endpoints: [start, end], labels });
        out.push(
          changes.update({
            target: seg,
            from: {
              endpoints: origEndpoints[edit.index],
              labels: origLabels[edit.index] ?? [],
            },
            to: { endpoints: [start, end], labels },
          })
        );
        break;
      }
      case "delete": {
        const seg = segments[edit.index];
        out.push(changes.remove(seg));
        schematic.wires.delete(seg, false);
        break;
      }
    }
  }

  for (const edit of edits) {
    if (edit.kind !== "create") continue;
    const data = schdata.WireSegment.new(edit.endpoints);
    const seg = schematic.wires.addProvisional(data);
    schematic.wires.clearProvisionals();
    // Through the merge/split path, not the raw insert meant for undo/redo
    // replay: a fresh segment laid over a collinear neighbour has to be
    // merged into it, and split where it meets a pin or a perpendicular
    // wire. `addMerging` hands the changes back so they join this
    // gesture's single undo entry rather than logging one of their own.
    out.push(...schematic.wires.addMerging(seg));
  }

  return out;
}

//
// # Plan Auditing
//
// Two things a wire plan must not do: leave illegal geometry, or change the
// circuit. Both are checked after every drag, not just when someone thinks to
// switch logging on — every defect found in this code was found by a check
// running, and the ones that got through were the rounds where nothing was
// watching. A net coming quietly apart is invisible on screen until much
// later, so it is worth a few milliseconds to catch at the source.
//
// Setting
//
//   window.GENALPHA_DEBUG_WIRES = true
//
// additionally logs each plan's inputs and edits, in the shape a
// `planWireEdits` test takes, so a report can be turned straight into one.
//

function verboseLogging(): boolean {
  return Reflect.get(globalThis, "GENALPHA_DEBUG_WIRES") === true;
}

export interface WirePlanAudit {
  wires: Array<[Point, Point]>;
  movingPinKeys: Set<string>;
  staticPinKeys: Set<string>;
  delta: Point;
  // Pin identities before the move, and which of them moved.
  pins: NetPin[];
  movingIds: Set<string>;
}

export function auditWirePlan(
  info: WirePlanAudit,
  edits: WireEdit[],
  after: WireSegment[]
): void {
  const pts = (p: Point) => [p.x, p.y];

  if (verboseLogging()) {
    // Stringified: the console collapses nested arrays to "Array(3)", which is
    // exactly the part needed to rebuild the case as a test.
    console.log(
      "[wire-plan] input",
      JSON.stringify({
        wires: info.wires.map(([a, b]) => [pts(a), pts(b)]),
        movingPinKeys: Array.from(info.movingPinKeys),
        staticPinKeys: Array.from(info.staticPinKeys),
        delta: pts(info.delta),
      })
    );
    console.log("[wire-plan] edits", JSON.stringify(edits));
    console.log(
      "[wire-plan] result",
      JSON.stringify(after.map((seg) => [pts(seg.start()), pts(seg.end())]))
    );
  }

  const violations = describeWireViolations(after, {
    allowAbutmentStubs: true,
  });
  if (violations) console.warn("[wire-plan] INVALID GEOMETRY\n" + violations);

  // Geometry can be perfectly legal and the circuit still wrong — a net coming
  // apart breaks none of the rules above.
  const before = info.wires.map(([a, b]) => ({ start: () => a, end: () => b }));
  const movedPins = info.pins.map((pin) =>
    info.movingIds.has(pin.id)
      ? { id: pin.id, loc: Point.add(pin.loc, info.delta) }
      : pin
  );
  const netChanges = describeNetChanges(
    netsOf(before, info.pins),
    netsOf(after, movedPins)
  );
  if (netChanges) console.warn("[wire-plan] NET CHANGED\n" + netChanges);
}
// Points a merge must not dissolve.
//
// Pin locations, because a pin's net is anchored by a wire *endpoint* inside
// its box — merging through one would leave the pin on a span, which is not a
// connection.
//
// And any point where a horizontal and a vertical wire meet. Two collinear
// wires butt-joined at such a point are held to the perpendicular wire *by
// their endpoints there*; fusing them into one would leave it merely crossing,
// and the branch would silently come off the net.
function protectedPoints(
  schematic: SchematicRender,
  wires: WireSegment[]
): Set<string> {
  const keys = schematic.wires.pinPointKeys();

  const horizontal: WireSegment[] = [];
  const vertical: WireSegment[] = [];
  for (const seg of wires) {
    const s = seg.start();
    const e = seg.end();
    if (s.y === e.y && s.x !== e.x) horizontal.push(seg);
    else if (s.x === e.x && s.y !== e.y) vertical.push(seg);
  }

  const touches = (seg: WireSegment, pt: Point): boolean => {
    const s = seg.start();
    const e = seg.end();
    if (s.x === e.x) {
      return (
        pt.x === s.x && pt.y >= Math.min(s.y, e.y) && pt.y <= Math.max(s.y, e.y)
      );
    }
    return (
      pt.y === s.y && pt.x >= Math.min(s.x, e.x) && pt.x <= Math.max(s.x, e.x)
    );
  };

  for (const seg of wires) {
    for (const pt of [seg.start(), seg.end()]) {
      if (
        horizontal.some((h) => touches(h, pt)) &&
        vertical.some((v) => touches(v, pt))
      ) {
        keys.add(pointKey(pt));
      }
    }
  }
  return keys;
}

// Collapse collinear overlaps left by a plan, returning the change-log entries
// for what it did.
//
// Two runs re-routing to the same anchor lay their final legs along the same
// line. Each route is individually correct — neither can see the other's
// geometry — so the redundancy has to be cleaned up after the fact rather than
// avoided during planning. `Wires.mergeCollinear` is the same merge that
// ordinary wire drawing uses.
//
// Pin locations are protected: a pin's net is anchored by a wire *endpoint*
// inside its box, so dissolving that endpoint into the interior of a longer
// wire would silently disconnect it. Where a merged span crosses such a point
// the merge is cut back apart there, which can call for a segment the merge did
// not have — hence the creator, whose additions join this gesture's undo entry.
export function mergeOverlaps(
  schematic: SchematicRender
): Array<changes.Single<WireSegment>> {
  const before = Array.from(schematic.wires.all());
  const origEndpoints = new Map<WireSegment, [Point, Point]>(
    before.map((seg) => [seg, [seg.start(), seg.end()]])
  );
  const created: WireSegment[] = [];

  const survivors = Wires.mergeCollinear(
    before,
    protectedPoints(schematic, before),
    (template, endpoints) => {
      const seg = schematic.wires.provisional({
        ...template.data,
        id: undefined,
        endpoints,
        labels: [],
      });
      schematic.wires.rawAdd(seg);
      created.push(seg);
      return seg;
    }
  );
  const kept = new Set(survivors);
  const out: Array<changes.Single<WireSegment>> = created.map((seg) =>
    changes.add(seg)
  );

  for (const seg of before) {
    const orig = origEndpoints.get(seg);
    if (!orig) continue;
    if (!kept.has(seg)) {
      out.push(changes.remove(seg));
      schematic.wires.delete(seg, false);
      continue;
    }
    const start = seg.start();
    const end = seg.end();
    if (Point.eq(orig[0], start) && Point.eq(orig[1], end)) continue;
    out.push(
      changes.update({
        target: seg,
        from: { endpoints: orig },
        to: { endpoints: [start, end] },
      })
    );
  }
  return out;
}
